import { Box, Button, ButtonProps, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { LineString } from "geojson";
import { FC, ReactNode, useCallback } from "react";
import { useNavigate } from "react-router";

import { TableFilterCheckbox } from "@nens/ranalib";
import { ThreediModel } from "@nens/threedi/openapi-client";

import { APP_BASE_HREF } from "../../App";
import { combineExtents, extentToLeaflet } from "../../utils/geo";
import ChooseSimulationButton from "../schematisations/ChooseSimulationButton";

import ExtentMap from "./ExtentMap";

export interface ThreediModelBlockProps {
  threediModel: ThreediModel;
  selectedRows: ThreediModel[];
  setSelectedRows: (rows: ThreediModel[]) => void;
}

const Title: FC<{ children: ReactNode }> = ({ children }) => (
  <Box fontWeight="bold" mb="4px" mt="4px" color="brand.analytic" textTransform="uppercase">
    {children}
  </Box>
);

interface ToSchematisationButtonProps extends ButtonProps {
  schematisationId: number;
  revisionId: number;
}

const ToSchematisationButton: FC<ToSchematisationButtonProps> = ({
  schematisationId,
  revisionId,
  ...rest
}) => {
  const navigate = useNavigate();
  const clickViewButton = useCallback(
    () => navigate(`${APP_BASE_HREF}/schematisations/${schematisationId}/${revisionId}`),
    [navigate, schematisationId, revisionId]
  );

  return (
    <Button variant="secondary" onClick={clickViewButton} {...rest}>
      View details
    </Button>
  );
};

const ThreediModelBlock: FC<ThreediModelBlockProps> = ({
  threediModel,
  selectedRows,
  setSelectedRows,
}) => {
  const isSelected = selectedRows.some((check) => check.id === threediModel.id);

  const onSelectChange = (checked: boolean) =>
    checked
      ? setSelectedRows([...selectedRows, threediModel])
      : setSelectedRows(selectedRows.filter((row: ThreediModel) => row.id !== threediModel.id));

  return (
    <Box
      borderWidth="thin"
      borderStyle="solid"
      borderColor="brand.border"
      borderRadius="4px"
      backgroundColor="brand.grey.100"
      minHeight="18rem"
      marginBottom="1rem"
      padding="1rem"
      zIndex={0}
      position="relative"
      w="100%"
      overflowX="auto"
    >
      <SimpleGrid columns={2} columnGap={3}>
        <Box minHeight="16rem" pl="16px" pt="4px">
          <Title>Model name</Title>
          <Text
            style={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              marginRight: "1rem",
            }}
            title={`${threediModel.name} (id: ${threediModel.id})`}
          >
            {threediModel.name} (id: {threediModel.id})
          </Text>
          <Title>Schematisation</Title>
          <Text>{threediModel.schematisationId || "-"}</Text>
          <Title>Revision</Title>
          <Text>{threediModel.revisionNumber}</Text>
          <Title>Created</Title>
          <Text>
            {threediModel.revisionCommitDate
              ? new Date(threediModel.revisionCommitDate).toLocaleString()
              : "-"}
          </Text>
        </Box>
        <Box minHeight="16rem" maxW="450px">
          {/*The awkward typing will be fixed with new Swagger*/}
          <ExtentMap
            extent={combineExtents([
              extentToLeaflet(threediModel.extentZeroD as LineString | null | undefined),
              extentToLeaflet(threediModel.extentOneD as LineString | null | undefined),
              extentToLeaflet(threediModel.extentTwoD as LineString | null | undefined),
            ])}
            borderWidth="thin"
            borderStyle="solid"
            borderColor="brand.glacier"
            boxShadow="0px 3px 6px #00000029"
          />
        </Box>

        <Flex marginTop="4" gap="8px" mt="32px">
          {threediModel.schematisationId && threediModel.revisionId ? (
            <ToSchematisationButton
              schematisationId={threediModel.schematisationId}
              revisionId={threediModel.revisionId}
            />
          ) : (
            <Button disabled>Cannot view details</Button>
          )}
          <ChooseSimulationButton modelId={threediModel.id} />
        </Flex>
        <Flex justifyContent="start" mt="32px">
          <TableFilterCheckbox
            m="0"
            id="select-model-checkbox"
            value={isSelected}
            onChange={onSelectChange}
          >
            Click to select
          </TableFilterCheckbox>
        </Flex>
      </SimpleGrid>
    </Box>
  );
};

export default ThreediModelBlock;
